/**
 * Column definitions for the PR Literature Review tables.
 *
 * A dedicated copy of the journal-articles column groups for the PR Literature
 * Review section. It differs in one way: the numeric PR topic score
 * (`scorePRTopic`, labelled "PR Score") is included in the compact and
 * extraction-status views (not just the full view), since the PR classifier
 * confidence is central to every PR review workflow. Keeping this separate from
 * the shared `journal-articles/columns.ts` means the Journal Articles tables are
 * unaffected.
 *
 * Provides per-view-mode column groups so the Manage Columns dialog only shows
 * columns relevant to the active table view.
 */

import { ColumnGroup } from "@/components/shared/ManageColumnsDialog"
import { ViewMode } from "@/components/shared/TableViewSelector"

/**
 * Column groups for the compact and expanded views.
 *
 * Mirrors the journal-articles compact view with `scorePRTopic` ("PR Score")
 * inserted after Collections so reviewers see the classifier confidence.
 */
const COMPACT_COLUMN_GROUPS: ColumnGroup<string>[] = [
  { keys: ["sourceId"], label: "Source ID" },
  { keys: ["title"], label: "Title" },
  { keys: ["topics"], label: "Topics" },
  { keys: ["collectionIds"], label: "Collections" },
  { keys: ["scorePRTopic"], label: "PR Score" },
  { keys: ["authors"], label: "Authors" },
  { keys: ["journaltitle"], label: "Journal" },
  { keys: ["publicationYear"], label: "Year" },
  { keys: ["studyYears"], label: "Study Years" },
  { keys: ["doi"], label: "DOI" },
  { keys: ["pubmedId"], label: "PubMed ID" },
  { keys: ["continents"], label: "Continents" },
  { keys: ["countries"], label: "Countries" },
]

/**
 * Column groups for the full view.
 * Shows every available column individually (already includes PR Score).
 */
const FULL_COLUMN_GROUPS: ColumnGroup<string>[] = [
  { keys: ["sourceId"], label: "Source ID" },
  { keys: ["title"], label: "Title" },
  { keys: ["topics"], label: "Topics" },
  { keys: ["excluded-topics"], label: "Excluded Topics" },
  { keys: ["collectionIds"], label: "Collections" },
  { keys: ["authors"], label: "Authors" },
  { keys: ["journaltitle"], label: "Journal" },
  { keys: ["publicationYear"], label: "Year" },
  { keys: ["studyYears"], label: "Study Years" },
  { keys: ["volume"], label: "Volume" },
  { keys: ["issue"], label: "Issue" },
  { keys: ["pages"], label: "Pages" },
  { keys: ["doi"], label: "DOI" },
  { keys: ["doiUrl"], label: "DOI URL" },
  { keys: ["pubmedId"], label: "PubMed ID" },
  { keys: ["pubmedUrl"], label: "PubMed URL" },
  { keys: ["ihmeNid"], label: "IHME NID" },
  { keys: ["mapSurveyId"], label: "MAP Survey ID" },
  { keys: ["sourceType"], label: "Source Type" },
  { keys: ["surveyType"], label: "Survey Type" },
  { keys: ["abstract"], label: "Abstract" },
  { keys: ["citation"], label: "Citation" },
  { keys: ["suggestedCitation"], label: "Suggested Citation" },
  { keys: ["url"], label: "URL" },
  { keys: ["city"], label: "City" },
  { keys: ["institution"], label: "Institution" },
  { keys: ["institutions"], label: "Institutions" },
  { keys: ["contacts"], label: "Contacts" },
  { keys: ["reportNumber"], label: "Report Number" },
  { keys: ["researchNotes"], label: "Research Notes" },
  { keys: ["usagePermission"], label: "Usage Permission" },
  { keys: ["communicationDate"], label: "Communication Date" },
  { keys: ["currentValue"], label: "Current Value" },
  { keys: ["dateUrlVisited"], label: "Date URL Visited" },
  { keys: ["createdBy"], label: "Created By" },
  { keys: ["createdDate"], label: "Created Date" },
  { keys: ["lastModifiedBy"], label: "Last Modified By" },
  { keys: ["lastModifiedDate"], label: "Last Modified Date" },
  { keys: ["prExtractionStatus"], label: "PR Extraction Status" },
  { keys: ["rsExtractionStatus"], label: "RS Extraction Status" },
  { keys: ["scorePRTopic"], label: "PR Score" },
  { keys: ["continents"], label: "Continents" },
  { keys: ["countries"], label: "Countries" },
]

/**
 * Column groups for the extraction status view.
 *
 * Mirrors the journal-articles status view with `scorePRTopic` ("PR Score")
 * inserted after Collections.
 */
const STATUS_COLUMN_GROUPS: ColumnGroup<string>[] = [
  { keys: ["sourceId"], label: "Source ID" },
  { keys: ["title"], label: "Title" },
  { keys: ["publicationYear"], label: "Year" },
  { keys: ["studyYears"], label: "Study Years" },
  { keys: ["topics"], label: "Topics" },
  { keys: ["collectionIds"], label: "Collections" },
  { keys: ["scorePRTopic"], label: "PR Score" },
  { keys: ["prExtractionStatus"], label: "PR Extraction Status" },
  { keys: ["rsExtractionStatus"], label: "RS Extraction Status" },
  { keys: ["continents"], label: "Continents" },
  { keys: ["countries"], label: "Countries" },
]

/**
 * Returns the column groups appropriate for the given view mode in the PR
 * Literature Review section.
 *
 * Only the three standard density modes are supported here (the section no
 * longer uses a locked "pr-bulk-review" mode); any other value falls back to
 * the compact groups.
 *
 * @param viewMode - The currently active view mode.
 * @returns Array of column groups for the Manage Columns dialog.
 */
export function getColumnGroupsForViewMode(viewMode: ViewMode): ColumnGroup<string>[] {
  if (viewMode === "full") {
    return FULL_COLUMN_GROUPS
  }
  if (viewMode === "status") {
    return STATUS_COLUMN_GROUPS
  }
  return COMPACT_COLUMN_GROUPS
}
